import { useState } from "react";
import type { RouterOutputs } from "@/lib/trpc";
import { Circle, Loader2, CheckCircle2, Target, ChevronDown, Plus, Pencil, Trash2, Link2, Unlink, Ban, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";
import { importanceToneClass } from "@/lib/importance";

/**
 * My Priorities (Home) — **status pills only, no ranking/scoring** (D3). Renders
 * the user's own priorities; tapping the status cycles via `priorities.setStatus`.
 *
 * Managers get a Mine | Team toggle and "+ Add priority". The Team view is
 * ALIGNMENT-ONLY: the server returns teammates' rows as assignment metadata
 * (no status field at all — see priorityPrivacy.ts), so this view renders
 * title + assignee with NO status pills, NO done counts and NO cycling.
 * Live progress is employee-owned; it reaches a manager only through an
 * approved receipt — the footer line says exactly that so the boundary
 * reads as deliberate, not broken.
 */
type Priorities = RouterOutputs["priorities"]["forWeek"];
type Priority = Priorities[number];
type Status = "pending" | "in_progress" | "done" | "dropped";
type View = "mine" | "team";

const COLLAPSED_COUNT = 3;

/** H/M/L — displayed under the UI label "Importance" (the API field remains
 *  `priority`; "priority" in UI copy means a work ITEM, never this level). */
const impLabel = (n: number, ar: boolean) =>
  (ar ? ["", "عالٍ", "متوسط", "منخفض"] : ["", "High", "Medium", "Low"])[n] ?? "";

/**
 * Default "mine" ordering (final-UX-polish pass): active items first, most
 * recently touched first, dropped items last. A just-created or just-edited
 * personal priority surfaces near the top — especially important inside the
 * collapse-to-3 view on mobile.
 *
 * Deliberately NOT part of the order: Importance (High/Medium/Low is a badge,
 * never a rank) and the workspace/team context (the same owner list must read
 * identically everywhere). Nothing is hidden — dropped rows sink, they never
 * disappear.
 *
 * `updatedAt` (bumped by every edit/status change) wins; `createdAt` is the
 * fallback; rows without either keep their relative order (stable sort).
 *
 * LINK-ACTIVITY ordering: linking/unlinking a personal priority does NOT bump
 * its `updatedAt` (the link lives in a separate table), so the optional
 * `activityAt` accessor lets the caller fold in a recent link/unlink activity
 * timestamp (see lib/personalPriorityActivity). It is MAXed with the row's own
 * timestamp — never lowers it — so an item the owner just re-connected floats
 * up. Dropped-rank still wins over ALL recency, so a recently linked dropped
 * item still sorts below active items.
 */
export function sortMyPriorities<T>(
  items: readonly T[],
  activityAt?: (item: T) => number | undefined,
): T[] {
  const touchedAt = (p: T): number => {
    const v = (p as any)?.updatedAt ?? (p as any)?.createdAt;
    const n = v == null ? Number.NaN : new Date(v as any).getTime();
    const base = Number.isFinite(n) ? n : 0;
    const act = activityAt?.(p);
    return act != null && Number.isFinite(act) && act > base ? act : base;
  };
  const droppedRank = (p: T): number =>
    (p as any)?.status === "dropped" ? 1 : 0;
  return [...items].sort(
    (a, b) => droppedRank(a) - droppedRank(b) || touchedAt(b) - touchedAt(a),
  );
}

function nextStatus(s: string | null | undefined): Status {
  if (s === "pending" || s === "dropped" || s == null) return "in_progress";
  if (s === "in_progress") return "done";
  return "pending"; // done → pending
}

function StatusPill({ status }: { status: string | null | undefined }) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  if (status === "done")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 px-2.5 py-0.5 text-xs font-medium">
        <CheckCircle2 className="h-3.5 w-3.5" /> {isAr ? "منجز" : "Done"}
      </span>
    );
  if (status === "in_progress")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-700 px-2.5 py-0.5 text-xs font-medium">
        <Loader2 className="h-3.5 w-3.5" /> {isAr ? "قيد التنفيذ" : "In progress"}
      </span>
    );
  if (status === "dropped")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground/70 px-2.5 py-0.5 text-xs font-medium line-through">
        <Ban className="h-3.5 w-3.5" /> {isAr ? "متروك" : "Dropped"}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground px-2.5 py-0.5 text-xs font-medium">
      <Circle className="h-3.5 w-3.5" /> {isAr ? "لم يبدأ" : "Not started"}
    </span>
  );
}

/** The owner's OWN connected-to state for one personal priority (Stage 3.5
 *  My-priorities loop). `teamId` = which team the OWN link points into (labels
 *  "this team" vs "another team direction"); title may be null only in
 *  degenerate cases and falls back to neutral copy. */
export type ConnectedTo = {
  teamPriorityId: number;
  title: string | null;
  teamId: number | null;
};

export function MyPrioritiesCard({
  priorities,
  view = "mine",
  showTeamToggle = false,
  onViewChange,
  onAddPriority,
  onCreate,
  nameById,
  busyId,
  onCycle,
  onEdit,
  onDelete,
  privateNote,
  connectedTo,
  currentTeamId,
  onConnect,
  onDisconnect,
  onDrop,
  allowDropped,
  recentFirst,
  activityById,
}: {
  priorities: Priority[];
  /** "mine" = own priorities, "team" = all members' (managers only). */
  view?: View;
  showTeamToggle?: boolean;
  /** Personal workspace — show the "private, visible only to you" framing
   *  (Stage 1 re-root: these live in the structurally-private personal table). */
  privateNote?: boolean;
  onViewChange?: (v: View) => void;
  /** Managers only — opens the Set-Team-Priorities dialog. */
  onAddPriority?: () => void;
  /** Personal surface — open the UNIFIED full create form (same
   *  PersonalPriorityEditorDialog as edit + create-for-this: Title,
   *  Description, Importance H/M/L). Replaces the old title-only inline add,
   *  which could not select an Importance (the no-Low bug). */
  onCreate?: () => void;
  /** userId → display name, for the Team view. */
  nameById?: Record<number, string>;
  busyId?: number | null;
  onCycle: (id: number, next: Status) => void;
  /** Personal workspace — edit / delete the caller's OWN priority (Stage 1).
   *  Icons render only when these callbacks are provided (mine view only). */
  onEdit?: (p: Priority) => void;
  onDelete?: (p: Priority) => void;
  /**
   * Stage 3.5 — the caller's OWN connected-to tags, keyed by personal priority
   * id (from the owner-only personalPriorityLinks.mineForWeek — INVERSE
   * orientation, one tag per personal priority; NEVER grouped/counted by team
   * priority — that shape is the forbidden Stage 3D rollup). Rendering the tag
   * requires the prop; other card usages are unchanged.
   */
  connectedTo?: Record<number, ConnectedTo[] | undefined>;
  /**
   * The SELECTED workspace's team id (null/undefined in a personal or no-team
   * context). Personal priorities are USER-owned, not team-owned — this prop
   * exists only to LABEL each own link relative to where the user is standing:
   * "Connected to this team" / "Connected to another team direction" /
   * (personal context) "Connected to team direction". Label, never hide.
   */
  currentTeamId?: number | null;
  /** Open the connect-to-direction picker for one OWN priority. */
  onConnect?: (p: Priority) => void;
  /** Unlink one OWN priority from its connected team direction. */
  onDisconnect?: (p: Priority, teamPriorityId: number) => void;
  /** Drop one OWN priority — opens a confirmation flow. When provided, the
   *  Drop control calls this instead of cycling straight to "dropped"; when
   *  omitted it falls back to the immediate onCycle(id,"dropped") (other
   *  usages / tests keep the old behavior). Restore stays immediate. */
  onDrop?: (p: Priority) => void;
  /** Expose the dropped-status control (mine view only). */
  allowDropped?: boolean;
  /** Opt-in default order for the OWN list: active first, most recently
   *  updated first, dropped last (see sortMyPriorities). Opt-in so other
   *  usages of this card (e.g. the capture sheet's weekly list) keep their
   *  server order untouched. */
  recentFirst?: boolean;
  /** Recent link/unlink activity timestamps keyed by personal-priority id
   *  (owner-only, client-only — see lib/personalPriorityActivity). Folded into
   *  the recentFirst order so a just-connected/disconnected item floats up.
   *  Only consulted when recentFirst is set. */
  activityById?: Record<number, number | undefined>;
}) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const [expanded, setExpanded] = useState(false);

  const teamView = view === "team";
  // Sort BEFORE the collapse slice so a fresh item lands inside the top-3.
  // Recent link/unlink activity (activityById) is folded in so a just-
  // connected/disconnected item floats up alongside edits and status changes.
  const ordered =
    recentFirst && !teamView
      ? sortMyPriorities(priorities, p => activityById?.[(p as any).id])
      : priorities;
  const hasMore = ordered.length > COLLAPSED_COUNT;
  const visible = expanded ? ordered : ordered.slice(0, COLLAPSED_COUNT);
  const doneCount = priorities.filter(
    p => (p as any).status === "done",
  ).length;

  return (
    <section className="rounded-3xl border border-border/60 bg-card p-5 shadow-sm" data-testid="priorities">
      <div className="flex items-center gap-2 text-[11px] font-semibold tracking-wider text-muted-foreground">
        <Target className="h-4 w-4 text-coral" />{" "}
        {teamView
          ? isAr ? "أولويات الفريق هذا الأسبوع" : "TEAM PRIORITIES THIS WEEK"
          : isAr ? "أولوياتي هذا الأسبوع" : "MY PRIORITIES THIS WEEK"}
      </div>

      {privateNote && !teamView ? (
        <p
          className="mt-1 text-[11px] text-muted-foreground"
          data-testid="prio-private-note"
        >
          {connectedTo
            ? isAr
              ? "خاصة بك. أولوياتك الشخصية لهذا الأسبوع تبقى معك عبر مساحات العمل. أدرها واربطها بتوجيه الفريق."
              : "Private to you. Your personal priorities for this week stay with you across workspaces. Manage them and connect them to team direction."
            : isAr
              ? "أولوياتك الخاصة. أنت وحدك تراها."
              : "Your private priorities. Only you can see them."}
        </p>
      ) : null}

      {/* Controls row: Mine|Team toggle (managers) + Add. On personal
          surfaces `onCreate` opens the UNIFIED full create form (same dialog
          as edit + create-for-this) — form-consistency contract. */}
      {showTeamToggle || onAddPriority || onCreate ? (
        <div className="mt-3 flex items-center justify-between gap-2">
          {showTeamToggle ? (
            <div className="inline-flex rounded-full border border-border bg-muted/40 p-0.5" data-testid="prio-toggle">
              {(["mine", "team"] as const).map(v => (
                <button
                  key={v}
                  onClick={() => onViewChange?.(v)}
                  className={cn(
                    "rounded-full px-3 py-0.5 text-xs font-medium capitalize transition-colors",
                    view === v ? "bg-coral text-white shadow-sm" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {isAr ? (v === "mine" ? "أولوياتي" : "الفريق") : v}
                </button>
              ))}
            </div>
          ) : (
            <span />
          )}
          {onCreate ? (
            <button
              onClick={onCreate}
              className="inline-flex items-center gap-1 text-xs font-medium text-coral hover:underline"
              data-testid="prio-create"
            >
              <Plus className="h-3.5 w-3.5" /> {isAr ? "إضافة هدف" : "Add a goal"}
            </button>
          ) : onAddPriority ? (
            <button
              onClick={onAddPriority}
              className="inline-flex items-center gap-1 text-xs font-medium text-coral hover:underline"
              data-testid="prio-add"
            >
              <Plus className="h-3.5 w-3.5" /> {isAr ? "إضافة أولوية" : "Add priority"}
            </button>
          ) : null}
        </div>
      ) : null}

      {priorities.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          {teamView
            ? isAr ? "لا توجد أولويات للفريق هذا الأسبوع." : "No team priorities this week."
            : isAr ? "لم يتم تحديد أولويات هذا الأسبوع." : "No priorities set this week."}
        </p>
      ) : (
        <>
          <ul className="mt-3 space-y-1">
            {visible.map(p => {
              const memberName = teamView ? nameById?.[(p as any).userId] : undefined;
              return (
                <li
                  key={p.id}
                  data-testid={`prio-row-${p.id}`}
                  className="rounded-2xl px-3 py-3 hover:bg-muted/40 transition-colors"
                >
                  {/* MOBILE-STACKED row (mobile-polish pass): content stacks
                      vertically — header, description, connections, then a
                      dedicated action footer. Nothing competes horizontally
                      with the title on narrow screens. */}
                  {/* Header: title (wraps, up to 2 lines) + importance badge. */}
                  <div className="flex items-start justify-between gap-2">
                    <span className="min-w-0 flex-1 text-sm font-medium break-words line-clamp-2">
                      {p.title}
                    </span>
                    {!teamView && (p as any).priority ? (
                      <span
                        className={cn(
                          "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
                          importanceToneClass((p as any).priority),
                        )}
                        title={isAr ? "الأهمية" : "Importance"}
                        data-testid={`prio-importance-${p.id}`}
                      >
                        {impLabel((p as any).priority, isAr)}
                      </span>
                    ) : null}
                  </div>
                  {memberName ? (
                    <span className="mt-0.5 block text-xs text-muted-foreground truncate">{memberName}</span>
                  ) : null}
                  {/* Description preview: readable WITHOUT entering edit mode;
                      bounded to 2 lines on mobile so a long note never
                      dominates the card. Own data only. */}
                  {!teamView && (p as any).description ? (
                    <p
                      className="mt-1 text-xs text-muted-foreground line-clamp-2"
                      data-testid={`prio-desc-${p.id}`}
                    >
                      {(p as any).description}
                    </p>
                  ) : null}
                  {/* Stage 3.5 + multi-link — the OWNER'S OWN connected-to
                      rows. ONE VISUAL ROW PER LINK (a personal priority may
                      be connected to several team directions); grouping is
                      render-layer only over flat per-link rows. NO numeric
                      summaries ever ("2 connections"/"+N more" are banned);
                      repeated rows are the count-free representation.
                      MOBILE ROW SHAPE: the team priority TITLE on its own line
                      (wraps, not aggressively truncated), then a line with the
                      contextual relationship label (left) + that link's own
                      Unlink (right, a safe tap target) — the label never
                      repeats the title. CONTEXTUAL labels (label, never hide):
                      team ws + same team    → "Connected to this team" (first)
                      team ws + other/departed team → "Connected to another
                                                team direction" (decision A:
                                                historical titles still show)
                      personal/no-team ws    → "Connected to team direction"
                      zero links (anywhere)  → ONE "Not connected to team
                                                direction" + plain Connect,
                                                and NO group header
                      Per-link Unlink removes ONLY that pair; siblings and the
                      personal priority itself remain. On linked items the
                      connect action reads "Connect another team direction".
                      Linked rows sit grouped under the COUNT-FREE owner-only
                      header "Connected team directions", left-aligned, with a
                      SUBTLE 1px rail (not a heavy line). */}
                  {!teamView && connectedTo
                    ? (() => {
                        // Current-team link first in a team workspace; the
                        // stable sort keeps createdAt order among the rest.
                        const links = [...(connectedTo[p.id] ?? [])].sort(
                          (a, b) =>
                            currentTeamId == null
                              ? 0
                              : (b.teamId === currentTeamId ? 1 : 0) -
                                (a.teamId === currentTeamId ? 1 : 0),
                        );
                        // Relationship label WITHOUT the title — the title
                        // has its own line, so nothing repeats per row.
                        const label = (cc: ConnectedTo) => {
                          if (currentTeamId == null) {
                            return isAr
                              ? "مرتبطة بتوجيه الفريق"
                              : "Connected to team direction";
                          }
                          return cc.teamId === currentTeamId
                            ? isAr
                              ? "مرتبطة بهذا الفريق"
                              : "Connected to this team"
                            : isAr
                              ? "مرتبطة بتوجيه فريق آخر"
                              : "Connected to another team direction";
                        };
                        // LEFT-ALIGNED connect action (block, never centered);
                        // wraps as a whole, never mid-phrase.
                        const connectBtn = (another: boolean) =>
                          onConnect ? (
                            <button
                              onClick={() => onConnect(p)}
                              data-testid={`prio-connect-${p.id}`}
                              className="inline-flex items-center gap-1 text-start text-[11px] font-medium text-coral py-1 hover:underline underline-offset-2"
                            >
                              <Link2 className="h-3 w-3 shrink-0" />
                              {another
                                ? isAr
                                  ? "اربط توجيه فريق آخر"
                                  : "Connect another team direction"
                                : isAr
                                  ? "اربطها"
                                  : "Connect"}
                            </button>
                          ) : null;
                        return links.length === 0 ? (
                          <div
                            className="mt-1.5"
                            data-testid={`prio-notconnected-${p.id}`}
                          >
                            <span className="block text-[11px] text-muted-foreground">
                              {isAr ? "غير مرتبطة بتوجيه الفريق" : "Not connected to team direction"}
                            </span>
                            {connectBtn(false)}
                          </div>
                        ) : (
                          <div
                            className="mt-2"
                            data-testid={`prio-connections-${p.id}`}
                          >
                            <span
                              className="block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80"
                              data-testid={`prio-connections-label-${p.id}`}
                            >
                              {isAr ? "توجيهات الفريق المرتبطة" : "Connected team directions"}
                            </span>
                            <div className="mt-1 space-y-2 border-s border-border/40 ps-2.5">
                              {links.map(c => (
                                <div
                                  key={c.teamPriorityId}
                                  data-testid={`prio-connected-${p.id}-${c.teamPriorityId}`}
                                >
                                  <span
                                    className="block text-xs font-medium text-foreground/90 break-words"
                                    data-testid={`prio-connected-title-${p.id}-${c.teamPriorityId}`}
                                  >
                                    {c.title ?? (isAr ? "توجيه الفريق" : "Team direction")}
                                  </span>
                                  <div className="mt-0.5 flex items-center justify-between gap-2">
                                    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                                      <Link2 className="h-3 w-3 shrink-0" />
                                      {label(c)}
                                    </span>
                                    {onDisconnect ? (
                                      <button
                                        onClick={() => onDisconnect(p, c.teamPriorityId)}
                                        data-testid={`prio-disconnect-${p.id}-${c.teamPriorityId}`}
                                        className="-me-1.5 inline-flex shrink-0 items-center gap-0.5 rounded-md px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
                                      >
                                        <Unlink className="h-3 w-3" /> {isAr ? "فك الربط" : "Unlink"}
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {onConnect ? <div className="mt-1">{connectBtn(true)}</div> : null}
                          </div>
                        );
                      })()
                    : null}
                  {/* Action FOOTER (mine view only): status pill on the LEFT,
                      edit / delete / drop on the RIGHT — separated so the pill
                      never collides with the icons; larger tap targets (p-2).
                      Team view is assignment metadata (no status, no actions). */}
                  {!teamView ? (
                    <div
                      className="mt-2.5 flex items-center justify-between gap-2"
                      data-testid={`prio-actions-${p.id}`}
                    >
                      <button
                        onClick={() => onCycle(p.id, nextStatus((p as any).status))}
                        disabled={busyId === p.id}
                        aria-label={isAr ? "تغيير الحالة" : "Cycle status"}
                        className="active:scale-95 transition-transform disabled:opacity-50"
                      >
                        <StatusPill status={(p as any).status} />
                      </button>
                      <div className="flex shrink-0 items-center gap-1.5">
                        {onEdit ? (
                          <button
                            onClick={() => onEdit(p)}
                            aria-label={isAr ? "تعديل" : "Edit"}
                            data-testid={`prio-edit-${p.id}`}
                            className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        ) : null}
                        {onDelete ? (
                          <button
                            onClick={() => onDelete(p)}
                            aria-label={isAr ? "حذف" : "Delete"}
                            data-testid={`prio-delete-${p.id}`}
                            className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        ) : null}
                        {allowDropped ? (
                          (p as any).status === "dropped" ? (
                            <button
                              onClick={() => onCycle(p.id, "pending")}
                              disabled={busyId === p.id}
                              aria-label={isAr ? "استعادة" : "Restore"}
                              data-testid={`prio-restore-${p.id}`}
                              className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => (onDrop ? onDrop(p) : onCycle(p.id, "dropped"))}
                              disabled={busyId === p.id}
                              aria-label={isAr ? "ترك" : "Drop"}
                              data-testid={`prio-drop-${p.id}`}
                              className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                            >
                              <Ban className="h-4 w-4" />
                            </button>
                          )
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>

          <div className="mt-2 flex items-center justify-between px-3">
            {hasMore ? (
              <button
                onClick={() => setExpanded(e => !e)}
                className="inline-flex items-center gap-1 text-xs font-medium text-coral hover:underline"
                data-testid="priorities-toggle"
              >
                {expanded
                  ? isAr ? "عرض أقل" : "Show less"
                  : isAr ? `عرض الكل ${priorities.length}` : `View all ${priorities.length}`}
                <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-180")} />
              </button>
            ) : (
              <span />
            )}
            {!teamView ? (
              <span className="text-[11px] text-muted-foreground tabular-nums">{doneCount}/{priorities.length} {isAr ? "منجز" : "done"}</span>
            ) : (
              <span />
            )}
          </div>
        </>
      )}

      {teamView ? (
        <p className="mt-2 text-[11px] text-muted-foreground" data-testid="prio-team-note">
          {isAr
            ? "يظهر التقدم عند مشاركته في ملخّص معتمد."
            : "Progress appears when shared in an approved receipt."}
        </p>
      ) : null}
    </section>
  );
}
